import React from 'react';
import TextareaAutosize from 'react-autosize-textarea';
import _ from 'lodash';
import { Field } from 'redux-form';
import { translate } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import Select from 'react-select';
import * as DateService from '../../utils/date';

import Button from '../../common/Button';

// Trigger form submit when the Enter key is pressed.
// {@link https://github.com/erikras/redux-form/issues/572#issuecomment-262618476}
const handleKeyDown = (e, cb) => {
  if (e.key === 'Enter' && e.shiftKey === false) {
    e.preventDefault();
    cb();
  }
};

const Textarea = (field) => (
  <TextareaAutosize
    value={field.input.value}
    onChange={(e) => field.input.onChange(e.target.value)}
    onFocus={(e) => field.input.onFocus(e.target.value)}
    onKeyDown={(e, handleSubmit) =>
      field.input.onKeyDown(e.target.value, handleSubmit)
    }
    {...field}
  />
);

const dueDates = _.times(7, (i) => {
  const value = DateService.getFirstDayOfTheYearsToCome(i - 2);

  return {
    value,
    label: DateService.getHumanizedYear(value).toString(),
  };
});

const DueDateSelect = (field) => (
  <Select
    isSearchable={false}
    classNamePrefix="c-select"
    className="c-select--secondary u-margin-vertical"
    value={
      dueDates.find((l) => l.value === field.input.value) ||
      dueDates.find(
        (l) => l.value === DateService.getFirstDayOfTheYearsToCome(0)
      )
    }
    onChange={(selectedOption) => field.input.onChange(selectedOption.value)}
    name={field.input.name}
    options={dueDates}
    {...field}
  />
);

const Form = ({
  id = Date.now(),
  goal,
  disabled,
  onCancel = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onFocus = () => {},
  onMemoAdd = () => {},
  handleSubmit,
  submitting,
  error,
  reset,
  shouldDisplayMenu = false,
  enableDelete = true,
  autoFocus = false,
  isCurrentUser,
  t,
}) => (
  <form onSubmit={handleSubmit} style={{ width: '100%' }}>
    <div className="u-display-flex u-align-items-center u-justify-content-between">
      <Field
        component="input"
        type="checkbox"
        name="status"
        id={id}
        className="c-checkbox"
        disabled={disabled}
      />
      <label htmlFor={id} className={disabled ? '' : 'u-cursor-pointer'} />
      <Field
        id={goal && goal._id}
        name="name"
        component={Textarea}
        className="c-input-secondary"
        placeholder={t('goal.create.placeholder')}
        rows={1}
        onFocus={onFocus}
        disabled={disabled}
        autoFocus={autoFocus}
        onKeyDown={(e) => handleKeyDown(e, handleSubmit)}
      />
      {disabled && isCurrentUser ? (
        <div>
          <Button
            type="button"
            styleType="update"
            className="u-padding-left-small"
            onClick={onMemoAdd}
            data-tip={t('goal.create.addUpdateTooltip')}
            data-for="goalUpdate"
          />
          <Button
            type="button"
            onClick={onEdit}
            styleType="edit-icon"
            data-tip={t('goal.create.editGoalTooltip')}
            data-for="goalUpdate"
          />
          <ReactTooltip id="goalUpdate" className="c-tooltip" effect="float" />
        </div>
      ) : (
        enableDelete &&
        isCurrentUser && (
          <div>
            <Button
              type="button"
              onClick={onDelete}
              styleType="delete"
              data-tip={t('goal.create.deleteGoalTooltip')}
              data-for="goalDelete"
            />
            <ReactTooltip
              id="goalDelete"
              className="c-tooltip"
              effect="float"
            />
          </div>
        )
      )}
    </div>

    {error && <p className="c-alert c-alert--danger u-margin-top">{error}</p>}

    {!disabled &&
      shouldDisplayMenu && (
        <div>
          <div className="u-display-flex u-flex-column">
            <div className="u-display-flex u-align-items-center u-justify-content-end">
              <p className="u-weight-light u-margin-bottom-none u-font-small u-margin-right-tiny u-color-gray">
                {t('goal.create.goalDueDateTitle')}
              </p>
              <Field name="dueAt" component={DueDateSelect} />
            </div>
          </div>
          <hr className="u-border-dashed" />
          {/* SMART Goals Guide */}
          <div className="u-margin-bottom u-text-left u-weight-light u-font-small">
            <p className="u-margin-bottom-tiny u-opacity-half">
              {t('goal.create.smartGoalsTitle')}
            </p>

            <ul className="c-list--secondary u-opacity-half">
              <li className="u-padding-vertical-micro">
                <span className="u-weight-bold">
                  <span role="img" aria-label="emoji">
                    📋
                  </span>{' '}
                  [ S ]pecific{' '}
                </span>{' '}
                - {t('goal.create.specificDescr')}
              </li>
              <li className="u-padding-vertical-micro">
                <span className="u-weight-bold">
                  <span role="img" aria-label="emoji">
                    📐
                  </span>{' '}
                  [ M ]easurable{' '}
                </span>{' '}
                - {t('goal.create.measurableDescr')}
              </li>
              <li className="u-padding-vertical-micro">
                <span className="u-weight-bold">
                  <span role="img" aria-label="emoji">
                    🎯
                  </span>{' '}
                  [ A ]chievable{' '}
                </span>{' '}
                - {t('goal.create.achievableDescr')}
              </li>
              <li className="u-padding-vertical-micro">
                <span className="u-weight-bold">
                  <span role="img" aria-label="emoji">
                    💛
                  </span>{' '}
                  [ R ]elevant{' '}
                </span>{' '}
                - {t('goal.create.relevantDescr')}
              </li>
              <li className="u-padding-vertical-micro">
                <span className="u-weight-bold">
                  <span role="img" aria-label="emoji">
                    ⏳
                  </span>{' '}
                  [ T ]ime Bound{' '}
                </span>{' '}
                - {t('goal.create.timeboundDescr')}
              </li>
            </ul>
            <p className="u-margin-left">
              <a
                href="https://www.mindtools.com/pages/article/smart-goals.htm"
                target="_blank"
                rel="noopener noreferrer"
                className="u-color-fountain-blue"
              >
                <span role="img" aria-label="emoji">
                  💡
                </span>{' '}
                {t('goal.create.learnMore')}
              </a>
            </p>
          </div>
          {/* End of SMART Goals Guide */}
          <div className="u-display-flex u-justify-content-end u-margin-top-small">
            <button
              onClick={() => {
                reset();
                onCancel();
              }}
              disabled={submitting}
              type="button"
              className="c-btn c-btn-simple c-btn-simple--secondary u-text-center"
            >
              {t('general.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="c-btn c-btn-simple c-btn-simple--primary u-text-center"
            >
              {submitting ? t('general.saving') : t('general.save')}
            </button>
          </div>
        </div>
      )}
  </form>
);

export default translate()(Form);



// WEBPACK FOOTER //
// ./src/goals/create/Form.js